import { NavigateFunction } from 'react-router-dom';
import { GoogleAuthApiResult } from '../services/googleAuth.api';
import { authTokenService, isPlatformAdminClaims } from '../services/auth.service';
import { resolvePostLoginPath } from './tenantPortalRouting';
import { navigateAppOrExternal } from './navigateAppOrExternal';
import { getAdminAppBaseUrl } from './adminAppUrl';

/**
 * Navigate after a successful POST /auth/google, preserving existing landlord/tenant gates.
 * Platform-admin accounts must use the admin host — never keep their JWT on this origin.
 */
export function navigateAfterGoogleAuth(
  result: GoogleAuthApiResult,
  navigate: NavigateFunction,
  options?: {
    redirectPath?: string;
    fromSignup?: boolean;
  }
): void {
  const claims = authTokenService.setToken(result.token);

  if (isPlatformAdminClaims(claims)) {
    authTokenService.clearToken();
    const adminUrl = getAdminAppBaseUrl() || 'https://admin.tenantplus.co.ke';
    const err: any = new Error(
      `Platform admin accounts sign in at ${adminUrl} — not the landlord app.`
    );
    err.code = 'USE_PLATFORM_ADMIN_HOST';
    throw err;
  }

  if (claims?.mustChangePassword || result.mustChangePassword) {
    navigate('/force-change-password', { replace: true });
    return;
  }

  if (result.accountKind === 'tenant') {
    if (result.mustVerifyPhone || claims?.mustVerifyPhone) {
      navigate('/tenant/verify-phone', { replace: true });
      return;
    }
    const portalId = claims?.tenantProfileId || result.user.tenantProfileId;
    if (portalId) {
      navigate(`/tenant/portal/${portalId}`, { replace: true });
    } else {
      navigate('/login?as=tenant', { replace: true });
    }
    return;
  }

  if (claims?.role === 'TENANT' || claims?.tenantProfileId) {
    navigateAppOrExternal(navigate, resolvePostLoginPath(claims, options?.redirectPath), {
      replace: true,
    });
    return;
  }

  // Landlord / staff
  if (claims?.accountStatus === 'RESTRICTED' && !options?.fromSignup) {
    navigate('/signup-pending-approval', { replace: true });
    return;
  }

  if (result.mustVerifyPhone || claims?.mustVerifyPhone) {
    navigate('/verify-phone', {
      replace: true,
      state: options?.fromSignup ? { fromSignup: true } : { fromLogin: true },
    });
    return;
  }

  if (options?.fromSignup) {
    navigate('/terms?signup=true', { replace: true });
    return;
  }

  navigateAppOrExternal(navigate, resolvePostLoginPath(claims, options?.redirectPath), {
    replace: true,
  });
}
